import { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Checkbox from "@mui/material/Checkbox";

import SoftButton from "components/SoftButton";

import DataTable from "cxfcomponents/Tables/DataTable"; 
import StatusBadge from "cxfcomponents/StatusBadge"; 
import ProductCellfromUser from "layouts/pages/users/reports/components/Reviews/ProductCell"; 

const AssignRoleModal = ({
  open,
  onClose,
  usersToDisplay, 
  roleName, 
  onAssign,
}) => {
  const [selectedUserIds, setSelectedUserIds] = useState([]); 

  useEffect(() => {
    if (open) {
      setSelectedUserIds([]);
    }
  }, [open]);

  const handleSelectUser = (userId, isChecked) => {
    if (isChecked) {
      setSelectedUserIds((prev) => [...prev, userId]);
    } else {
      setSelectedUserIds((prev) => prev.filter((id) => id !== userId));
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allUserIds = usersToDisplay.map((user) => user.auth0_id);
      setSelectedUserIds(allUserIds);
    } else {
      setSelectedUserIds([]);
    }
  };

  const isAllSelected =
    usersToDisplay && usersToDisplay.length > 0 && selectedUserIds.length === usersToDisplay.length;
  const isIndeterminate = selectedUserIds.length > 0 && !isAllSelected;

  const dataTableData = useMemo(() => {
    const safeUsersToDisplay = Array.isArray(usersToDisplay) ? usersToDisplay : [];

    return {
      columns: [
        {
          Header: (
            <Checkbox
              checked={isAllSelected}
              indeterminate={isIndeterminate}
              onChange={handleSelectAll}
            />
          ),
          accessor: "select",
          disableSortBy: true,
          width: "5%",
          Cell: ({ row }) => {
            const userId = row.original?.auth0_id; 
            return (
              <Checkbox
                checked={selectedUserIds.includes(userId)}
                onChange={(event) => handleSelectUser(userId, event.target.checked)}
              />
            );
          },
        },
        {
          Header: "User",
          accessor: "userDisplay", 
          Cell: ({ row }) => (
            <ProductCellfromUser
              id={row.original?.auth0_id}
              image={row.original?.image}
              name={`${row.original?.first_name ?? ""} ${row.original?.last_name ?? ""}`}
              firstName={row.original?.first_name}
              lastName={row.original?.last_name}
              email={row.original?.email_address}
              checked={selectedUserIds.includes(row.original?.auth0_id)}
            />
          ),
        },
        {
          Header: "Status",
          accessor: "status",
          Cell: ({ value }) => <StatusBadge status={value} />,
        },
        // Add other relevant columns if needed
      ],
      rows: safeUsersToDisplay.map((user) => ({

        select: user.auth0_id, 
        userDisplay: user, 
        status: user.status,
        auth0_id: user.auth0_id,
        image: user.image,
        first_name: user.first_name,
        last_name: user.last_name,
        email_address: user.email_address,
      })),
    };
  }, [usersToDisplay, selectedUserIds]); 

  const handleAssignClick = () => {
    if (selectedUserIds.length > 0) {
      onAssign(selectedUserIds);
    }
  };

  return (
    <Dialog
      fullWidth
      open={open}
      onClose={onClose}
      PaperProps={{
        style: {
          maxWidth: "800px", 
        },
      }}
    >
      <DialogTitle>
        {roleName ? `Assign Users to Role: ${roleName}` : "Assign Users to Role"}
      </DialogTitle>
      <DialogContent dividers>
        {" "}
        <DataTable
          table={dataTableData}
          entriesPerPage={{ defaultValue: 25 }}
          canSearch 
          hiddenTopButtons={true}
          noTopPart={true}
        />
      </DialogContent>
      <DialogActions>
        <SoftButton variant="outlined" color="secondary" onClick={onClose}>
          Cancel
        </SoftButton>
        <SoftButton
          variant="gradient"
          color="info"
          onClick={handleAssignClick}
          disabled={selectedUserIds.length === 0} 
        >
          Assign Selected ({selectedUserIds.length})
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
};

AssignRoleModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  usersToDisplay: PropTypes.arrayOf(PropTypes.object).isRequired,
  roleName: PropTypes.string,
  onAssign: PropTypes.func.isRequired,
};

export default AssignRoleModal;
